"use client";

import { backendClient } from "@/lib/backend-api";
import { useAeoBrainGenerationInFlightStore } from "@/store/aeo-brain-generation-in-flight-store";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { toast } from "sonner";

const GENERATING_FUNNELS_TOAST_ID = "generating-funnels";

export function useAeoBrainGlobalGenerationSync() {
  const aeoBrainInFlight = useAeoBrainGenerationInFlightStore(
    (state) => state.aeoBrainInFlight,
  );
  const endAeoBrainGeneration = useAeoBrainGenerationInFlightStore(
    (state) => state.endAeoBrainGeneration,
  );
  const lastPendingReviewCountRef = useRef<number | null>(null);

  const { data: pendingReviewCount } = useQuery({
    queryKey: ["aeo-pending-review-count-global"],
    queryFn: async () => {
      const res = await backendClient.funnels.list({
        page: 1,
        pageSize: 1,
        isAiDiscoveryCampaign: true,
      });
      return res.statusCounts?.pending ?? 0;
    },
    enabled: aeoBrainInFlight > 0,
    refetchInterval: aeoBrainInFlight > 0 ? 4000 : false,
  });

  useEffect(() => {
    if (aeoBrainInFlight > 0) {
      toast.loading(`Generating ${aeoBrainInFlight} Funnel(s)...`, {
        id: GENERATING_FUNNELS_TOAST_ID,
        dismissible: false,
        duration: 10 * 60 * 1000,
      });
    } else {
      toast.dismiss(GENERATING_FUNNELS_TOAST_ID);
      lastPendingReviewCountRef.current = null;
    }

    if (aeoBrainInFlight === 0 || pendingReviewCount === undefined) {
      return;
    }

    if (lastPendingReviewCountRef.current === null) {
      lastPendingReviewCountRef.current = pendingReviewCount;
      return;
    }

    const delta = pendingReviewCount - lastPendingReviewCountRef.current;
    if (delta <= 0) {
      lastPendingReviewCountRef.current = pendingReviewCount;
      return;
    }

    const completionsToAcknowledge = Math.min(delta, aeoBrainInFlight);
    lastPendingReviewCountRef.current = pendingReviewCount;
    for (let i = 0; i < completionsToAcknowledge; i++) {
      endAeoBrainGeneration();
    }
  }, [aeoBrainInFlight, pendingReviewCount, endAeoBrainGeneration]);
}
